import React, { useEffect, useState } from 'react';
import { RangeCriteriaForm, RangeCriteriaFormState } from './RuleForm.RangeCriteriaForm';
import MeasurementCriteriaForm, { MeasurementsFormState } from './RuleForm.MeasurementCriteriaForm';
import { GeolocationCriteriaForm, GeolocationFormState, SelectProps } from './RuleForm.GeolocationCriteriaForm';
import { CountryCriteriaForm, CountryCriteriaFormState } from './RuleForm.CountryCriteriaForm';
import { AppMultiselect, AppSelectItemProp } from '../../../components/molecules/AppMultiselect/AppMultiselect';
import { SwitchCriteriaForm } from './RuleForm.SwitchCriteriaForm';
import AppSelect from '../../../components/atoms/AppSelect/AppSelect';
import { CriteriaList } from '../../../interfaces/rule';
import AppInput from '../../../components/atoms/AppInput/AppInput';
import IconTrash from '../../../assets/trash.png';
import { isDDUDDP, isPRC } from '../../../utils/Rules/CriteriaValidator';
import PostalCodeForm from './RuleForm.PostalCodeForm';
import AppSwitchToggle from 'components/molecules/AppSwitchToggle/AppSwitchToggle';
import HsCodeForm from '../../../templates/presentation/components/molecules/HsCodeForm/HsCodeForm';

export interface CriteriaOptionExtends extends CriteriaList {
  countries?: SelectProps[];
  serviceCode?: SelectProps[];
  cities?: SelectProps[];
  countriesList?: SelectProps[];
}

interface RuleFormOptionFormState {
  criteriaKey: string,
  criteriaId: number,
  value: undefined | RangeCriteriaFormState | MeasurementsFormState | GeolocationFormState | CountryCriteriaFormState | string | boolean | AppSelectItemProp[],
}

interface RuleFormOptionOption {
  criterias: CriteriaOptionExtends[];
  disabledOptions?: string[];
  hasErrors?: { [key: string]: string };
  value?: RuleFormOptionFormState;
  onChange: (state: RuleFormOptionFormState) => void;
  onDelete?: (criteriaId: number) => void;
  onCountryChange?: (criteriaKey: string, countryCode: string) => void;
  loadingCities?: string | null;
  isLoading?: boolean;
}

export const RuleFormOption = ({
                                 criterias,
                                 onChange,
                                 onDelete,
                                 onCountryChange,
                                 loadingCities,
                                 disabledOptions,
                                 hasErrors,
                                 value,
                               }: RuleFormOptionOption) => {
  const [isUpdateValue, setIsUpdateValue] = useState(false);
  const [formState, setFormState] = useState<RuleFormOptionFormState>(value || {
    criteriaKey: '',
    criteriaId: 0,
    value: undefined,
  });

  useEffect(() => {
    onChange(formState);
  }, [formState]);

  const switchCriteria = (criteriaKey: string) => {
    const criteria = criterias.find(c => c.key === criteriaKey);

    const unit = {
      'weight': 'Kg',
      'declared_value': '',
      'postal_code_range': '',
    };


    if (criteria)
      switch (criteriaKey) {
        case 'weight':
        case 'declared_value':
        case 'postal_code_range': {
          const initialValue = !isUpdateValue && typeof formState?.value === 'object' && 'from' in formState.value ? formState.value : undefined;
          return (
            <div key={criteriaKey} data-testid="componente-a">
              <RangeCriteriaForm
                value={initialValue}
                onChange={(e) => setFormState({ ...formState, value: e, criteriaId: criteria.id })}
                placeholder={['Desde', 'Hasta']}
                hasError={hasErrors ? hasErrors[criteria.id] : undefined}
                addonRight={<div style={{ color: '#94A3B8' }}>{unit[criteriaKey]}</div>}
              />
            </div>
          );
        }
        case 'measurements': {
          const initialValue = !isUpdateValue && typeof formState?.value === 'object' && 'width' in formState.value ? formState.value : undefined;
          return <div data-testid="measurement-form">
            <MeasurementCriteriaForm value={initialValue} onChange={(e) => setFormState({
              ...formState,
              value: e,
              criteriaId: criteria.id,
            })} />
          </div>;
        }
        case 'states':
        case 'town':
        case 'township': {
          const initialValue: GeolocationFormState | undefined =
            !isUpdateValue &&
            typeof formState?.value === 'object' &&
            formState.value !== null &&
            'countryCode' in formState.value &&
            'values' in formState.value
              ? (formState.value as GeolocationFormState)
              : undefined;
          return (
            <div key={criteriaKey} data-testid="componente-b">
              {criteria.countries ?
                <GeolocationCriteriaForm
                  key={criteriaKey}
                  value={initialValue}
                  cities={criteria.cities ? criteria.cities : []}
                  countries={criteria.countries}
                  citiesTitle={criterias.find(c => c.key === formState.criteriaKey)?.name || ''}
                  loadingCities={loadingCities === criteriaKey}
                  onCountryChange={(countryCode) => onCountryChange?.(criteriaKey, countryCode)}
                  onChange={(e) => setFormState({
                    ...formState,
                    value: e,
                    criteriaId: criteria.id,
                  })} /> : 'Cargando...'}

            </div>
          );
        }

        case 'postal_code_list':
          return <PostalCodeForm
            onChange={(file) => setFormState({ ...formState, value: file, criteriaId: criteria.id })} />;
        case 'country': {
          const initialValue = !isUpdateValue && typeof formState?.value === 'object' && formState.value !== null && 'selectedCountries' in formState.value
            ? (formState.value as CountryCriteriaFormState)
            : undefined;
          return (
            <div key={criteriaKey} data-testid="country-criteria-form">
              {criteria.countriesList != null ? (
                <CountryCriteriaForm
                  countries={criteria.countriesList}
                  value={initialValue}
                  onChange={(e) =>
                    setFormState({ ...formState, value: e, criteriaId: criteria.id })
                  }
                />
              ) : (
                'Cargando...'
              )}
            </div>
          );
        }
        case 'hs_code':
          return <HsCodeForm
            onChange={(file) => setFormState({ ...formState, value: file, criteriaId: criteria.id })} />;
        case 'distribution_provider':
        case 'service_code':
        case 'service_types':
        case 'clients': {
          const initialValue = !isUpdateValue && Array.isArray(formState.value) ? formState.value.map(v => v.value) : [];
          return <div data-testid="componente-c" className="w-[275px]">
            {criteria.serviceCode && <>Código</>}
            {criteria.serviceCode ? <AppMultiselect isSearchable
                                                    options={criteria.serviceCode}
                                                    onChange={(e) => {
                                                      setFormState({ ...formState, value: e, criteriaId: criteria.id });
                                                    }}
                                                    value={criteria.serviceCode.filter(s => initialValue.includes(s.value))} /> : 'Cargando...'}

          </div>;
        }
        case 'ddu_ddp': {
          const initialValue = !isUpdateValue && typeof formState.value === 'boolean' ? formState.value : false;
          return (
            <div data-testid="componente-d" className="justify-self-end">
              <SwitchCriteriaForm
                checkSuffix={'DDP'}
                checkPrefix={'DDU'}
                onChange={(e) => {
                  setFormState({ ...formState, value: e, criteriaId: criteria.id });
                }}
                checked={initialValue} />
            </div>
          );
        }

        case 'prc': {
          const initialValue = !isUpdateValue && typeof formState.value === 'boolean' ? formState.value : true;
          return (
            <div data-testid="componente-prc" className="flex items-center h-10 w-[165px]">
              <AppSwitchToggle
                checked={initialValue}
                onChange={(e) => {
                  setFormState({ ...formState, value: e, criteriaId: criteria.id });
                }}
              />
            </div>
          );
        }

        default:
          return <></>;
      }
  };

  return <div className={'flex gap-3'}>
    <div className="flex items-end w-[275px]">
      <AppSelect
        placeholder={'Seleccionar'}
        label={'Tipo'}
        value={formState.criteriaKey}
        options={criterias.map(c => ({ value: c.key, label: c.name, disabled: disabledOptions?.includes('' + c.key) }))}
        onChange={({ target }) => {
          setIsUpdateValue(true);
          const criteria = criterias.find(c => c.key === target.value);
          if (criteria) {
            const state = {
              ...formState,
              criteriaKey: target.value,
              criteriaId: criteria.id,
            };
            if (isDDUDDP(target.value))
              state.value = false;
            if (isPRC(target.value))
              state.value = true;
            setFormState({ ...formState, ...state });
          }
        }}
      />
    </div>

    <div className="flex items-end w-full">
      {(formState.criteriaKey) ? <div className={'flex w-full justify-between'}>
        {switchCriteria(formState.criteriaKey)}
      </div> : <div className="flex w-full gap-3">
        <AppInput
          className={'w-12  lg:w-24 2xl:w-32'}
          placeholder={'Seleccionar'}
          disabled
        />
        <AppInput
          className={'w-12  lg:w-24 2xl:w-32'}
          placeholder={'Seleccionar'}
          disabled
        />
      </div>}

      {onDelete && <div
        onClick={() => {
          const criteriaFind = criterias.find(c => c.key === formState.criteriaKey);
          onDelete(criteriaFind ? criteriaFind.id : 0);
        }}
        className="border rounded-md flex justify-center items-center w-[40px] h-[40px] cursor-pointer hover:bg-[#E2E8F0] transition-all duration-300"
      >
        <img src={IconTrash} alt="Delete" />
      </div>}
    </div>
  </div>;
};
